import React, { useMemo, useCallback, useRef, useState, useEffect } from "react"
import { renderToStaticMarkup } from "react-dom/server"
import Timeline, { TimelineHeaders, SidebarHeader, DateHeader } from "react-calendar-timeline"
import "react-calendar-timeline/dist/style.css"
import { Box, Button, Center, HStack, Heading, Icon, VStack } from "@chakra-ui/react"
import { FaChartLine, FaChevronDown, FaChevronRight, FaInfo } from "react-icons/fa6"
import { MdDoNotDisturbOn } from "react-icons/md"
import { endOfDay, startOfDay } from "date-fns"
import { format as formatDate } from "date-fns"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import "./SitesTimelineView.scss"
import "./TimelineView.scss"
import ActivityCellHover from "./ActivityCellHover"
import OrderCellHover from "./OrderCellHover"
import {
  transformToSiteGroups,
  getSiteFromActivity,
  getSiteKey,
  type SiteGroup,
  type AssetCalendarData,
  type ActivityWithSite,
  type SuggestedFulfilment,
  type ConsumptionPoint,
} from "./siteGroupingUtils"

// Extract the Order type from GetOrdersQuery result
type OrderFromQuery = NonNullable<gqlTypes.GetOrdersQuery['orders']['data'][number]>
import { ACTIVITY_KIND_CONFIGS } from "./TimelineView"
import { formatActivityKind } from "@hydrologiq/shared/utils/activities/activityStatus"
import AssetLink from "@hydrologiq/shared/asset/AssetLink"
import { ConsumptionLineChart } from "./ConsumptionLineChart"
import { toUserTZ } from "@hydrologiq/common/helpers/date"

const formatDateTime = (date: number | string | null | undefined): string => {
  if (!date) return "—"
  const dateObj = new Date(date)
  if (isNaN(dateObj.getTime())) return "—"
  return formatDate(dateObj, "dd/MM/yyyy HH:mm")
}

interface ApiResponse {
  data: AssetCalendarData[]
  meta: { date_range: { start: string; end: string } }
}

interface SitesTimelineViewProps {
  data: ApiResponse
  visibleTimeStart: number
  visibleTimeEnd: number
  filteredAssetIds: string[]
  suggestedFulfilments?: SuggestedFulfilment[]
  siteResponses?: Map<string, any>
  orders?: OrderFromQuery[]
  onTimeChange: (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: (start: number, end: number) => void
  ) => void
  onItemSelect?: (itemId: string) => void
  onActivityEdit?: (activityId: string, assetId: string) => void
  onSwitchToAssetsView?: () => void
  onConfirmSuggestedFulfilment?: (suggestion: SuggestedFulfilment) => void
}

const ActivityHoverOverlay = React.memo(
  React.forwardRef((_, ref) => {
    const [state, setState] = React.useState<{
      activity: ActivityWithSite | null
      left: number
      top: number
      settings: { icon: React.ComponentType; textColor: string; backgroundColor: string } | null
      visible: boolean
    }>({
      activity: null,
      left: 0,
      top: 0,
      settings: null,
      visible: false,
    })

    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    React.useImperativeHandle(ref, () => ({
      show: (
        activity: ActivityWithSite,
        left: number,
        top: number,
        settings: { icon: React.ComponentType; textColor: string; backgroundColor: string }
      ) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState({ activity, left, top, settings, visible: true })
      },
      hide: () => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = setTimeout(() => {
          setState((s) => ({ ...s, visible: false }))
        }, 50)
      },
      updatePosition: (left: number, top: number | null) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState((s) => ({ ...s, left, top: top ?? s.top }))
      },
    }))

    if (!state.visible || !state.activity || !state.settings) return null

    return (
      <ActivityCellHover
        activity={state.activity as any}
        icon={state.settings.icon}
        left={state.left}
        top={state.top}
        color={state.settings.textColor}
        backgroundColor={state.settings.backgroundColor}
        open={true}
      >
        <Box />
      </ActivityCellHover>
    )
  })
)

const OrderHoverOverlay = React.memo(
  React.forwardRef((_, ref) => {
    const [state, setState] = React.useState<{
      order: gqlTypes.AssetActivityOrder | null
      quantity: number
      quantityPlanned: number
      percentPlanned: number
      startDate: string | null
      endDate: string | null
      left: number
      top: number
      visible: boolean
    }>({
      order: null,
      quantity: 0,
      quantityPlanned: 0,
      percentPlanned: 0,
      startDate: null,
      endDate: null,
      left: 0,
      top: 0,
      visible: false,
    })

    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    React.useImperativeHandle(ref, () => ({
      show: (
        order: gqlTypes.AssetActivityOrder,
        quantity: number,
        quantityPlanned: number,
        percentPlanned: number,
        startDate: string | null,
        endDate: string | null,
        left: number,
        top: number
      ) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState({ order, quantity, quantityPlanned, percentPlanned, startDate, endDate, left, top, visible: true })
      },
      hide: () => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = setTimeout(() => {
          setState((s) => ({ ...s, visible: false }))
        }, 50)
      },
      updatePosition: (left: number, top: number | null) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState((s) => ({ ...s, left, top: top ?? s.top }))
      },
    }))

    if (!state.visible || !state.order) return null

    return (
      <OrderCellHover
        order={state.order}
        quantity={state.quantity}
        quantityPlanned={state.quantityPlanned}
        percentPlanned={state.percentPlanned}
        startDate={state.startDate ?? undefined}
        endDate={state.endDate ?? undefined}
        left={state.left}
        top={state.top}
        open={true}
      >
        <Box />
      </OrderCellHover>
    )
  })
)

const SitesTimelineView: React.FC<SitesTimelineViewProps> = ({
  data,
  visibleTimeStart,
  visibleTimeEnd,
  filteredAssetIds,
  suggestedFulfilments = [],
  siteResponses = new Map(),
  orders = [],
  onTimeChange,
  onActivityEdit,
  onSwitchToAssetsView,
  onConfirmSuggestedFulfilment,
}) => {

  const hoverOverlayRef = useRef<{
    show: (a: ActivityWithSite, l: number, t: number, s: any) => void
    hide: () => void
    updatePosition: (l: number, t: number | null) => void
  }>(null)
  const orderHoverOverlayRef = useRef<{
    show: (
      order: gqlTypes.AssetActivityOrder,
      quantity: number,
      quantityPlanned: number,
      percentPlanned: number,
      startDate: string | null,
      endDate: string | null,
      left: number,
      top: number
    ) => void
    hide: () => void
    updatePosition: (l: number, t: number | null) => void
  }>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [expandedSites, setExpandedSites] = useState<Record<string, boolean>>({})
  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null)

  const siteGroups = useMemo(() => {
    const filtered = data.data.filter(
      (d) => filteredAssetIds.length === 0 || filteredAssetIds.includes(d.asset.id)
    )
    return transformToSiteGroups(filtered as AssetCalendarData[], true, visibleTimeStart, visibleTimeEnd, suggestedFulfilments, orders, siteResponses)
  }, [data.data, filteredAssetIds, visibleTimeStart, visibleTimeEnd, suggestedFulfilments, orders, siteResponses])

  const toggleSite = useCallback((siteId: string) => {
    setExpandedSites((prev) => ({ ...prev, [siteId]: !(prev[siteId] ?? true) }))
  }, [])

  const { groups, items } = useMemo(() => {
    const timelineGroups: Array<{
      id: string
      title: string
      groupType: "site-header" | "asset"
      siteGroup?: SiteGroup
      assetData?: AssetCalendarData
      height: number
      siteIndex: number
    }> = []
    const timelineItems: Array<{
      id: string
      group: string
      title: string
      start_time: number
      end_time: number
      itemType: "order" | "activity" | "fulfilment" | "suggested_fulfilment" | "consumption_chart"
      itemProps: Record<string, unknown>
      activity?: ActivityWithSite
      asset?: gqlTypes.Asset
      order?: gqlTypes.AssetActivityOrder
      suggestedFulfilment?: SuggestedFulfilment
      consumptionTimeline?: ConsumptionPoint[]
    }> = []

    for (let siteIdx = 0; siteIdx < siteGroups.length; siteIdx++) {
      const sg = siteGroups[siteIdx]
      const siteId = sg.site?.id ?? "__unassigned__"
      const siteName = sg.site?.name ?? "Unassigned"
      const isExpanded = expandedSites[siteId] !== false

      timelineGroups.push({
        id: `site-${siteId}-header`,
        title: siteName,
        groupType: "site-header",
        siteGroup: sg,
        height: sg.hasIotData ? (isExpanded ? 100 : 55) : 55,
        siteIndex: siteIdx,
      })

      // Add tank level chart item if site has IoT data and is expanded
      if (sg.hasIotData && isExpanded && sg.consumptionTimeline.length > 0) {
        timelineItems.push({
          id: `tank-level-chart-${siteId}`,
          group: `site-${siteId}-header`,
          title: "",
          start_time: visibleTimeStart,
          end_time: visibleTimeEnd,
          itemType: "consumption_chart",  // Keep same type for now to avoid breaking other code
          itemProps: {},
          consumptionTimeline: sg.consumptionTimeline,
        })
      }

      for (const order of sg.orders) {
        if (!order.start) continue
        const startTime = toUserTZ(order.start).unixUtcMillis()
        const endTime = order.end
          ? endOfDay(toUserTZ(order.end).toDate()).getTime()
          : Math.max(visibleTimeEnd, startTime + 1)
        const quantity = order.quantity ?? 0
        const quantityPlanned = order.quantityPlanned ?? 0
        const percentPlanned =
          quantity > 0
            ? Math.round((quantityPlanned / quantity) * 100)
            : 100

        timelineItems.push({
          id: `order-${siteId}-${order.id}`,
          group: `site-${siteId}-header`,
          title: `${order.name}: ${quantityPlanned} kg`,
          start_time: startTime,
          end_time: endTime,
          itemType: "order",
          itemProps: {
            "data-order-id": order.id,
            "data-percent-planned": percentPlanned,
          },
          order,
        })
      }

      for (const fulfilment of sg.fulfilments) {
        if (!fulfilment.start || !fulfilment.end) continue
        const startTime = startOfDay(new Date(fulfilment.start)).getTime()
        const endTime = endOfDay(new Date(fulfilment.end)).getTime()

        timelineItems.push({
          id: `fulfilment-${siteId}-${fulfilment.id}`,
          group: `site-${siteId}-header`,
          title: `Fulfilment: ${fulfilment.order?.name || 'Unknown'}`,
          start_time: startTime,
          end_time: endTime,
          itemType: "fulfilment",
          itemProps: {
            "data-fulfilment-id": fulfilment.id,
            "data-order-id": fulfilment.order_id,
            "data-activity-kind": "fulfilment",
          },
          activity: fulfilment,
        })
      }

      // Add suggested fulfilments (ghost bars)
      // Find the chronologically first suggestion (smallest days_until)
      const firstSuggestionStartDate = sg.suggestedFulfilments.length > 0
        ? sg.suggestedFulfilments.reduce((earliest, s) =>
          s.days_until < earliest.days_until ? s : earliest
        ).suggested_start_date
        : null

      for (let i = 0; i < sg.suggestedFulfilments.length; i++) {
        const suggested = sg.suggestedFulfilments[i]
        const isFirstSuggestion = suggested.suggested_start_date === firstSuggestionStartDate
        const startDate = new Date(suggested.suggested_start_date)
        const endDate = new Date(suggested.suggested_end_date)
        const startTime = startOfDay(startDate).getTime()
        const endTime = endOfDay(endDate).getTime()

        timelineItems.push({
          id: `suggested-${siteId}-${suggested.suggested_start_date}`,
          group: `site-${siteId}-header`,
          title: `~${suggested.suggested_quantity} kg`,
          start_time: startTime,
          end_time: endTime,
          itemType: "suggested_fulfilment",
          itemProps: {
            "data-suggested": "true",
            "data-confidence": suggested.confidence,
            "data-forecast-method": suggested.forecast_method,
            "data-is-first": isFirstSuggestion,
          },
          suggestedFulfilment: suggested,
        })
      }

      if (isExpanded) {
        for (const assetData of sg.assets) {
          const asset = assetData.asset as gqlTypes.Asset
          timelineGroups.push({
            id: `site-${siteId}-asset-${asset.id}`,
            title: asset.name,
            groupType: "asset",
            siteGroup: sg,
            assetData,
            height: 60,
            siteIndex: siteIdx,
          })

          const siteKey = getSiteKey(sg.site)
          const activitiesAtSite = assetData.activities.filter(
            (a) => getSiteKey(getSiteFromActivity(a)) === siteKey
          )

          const activitiesByDay = new Map<string, (ActivityWithSite & { originalActivity: ActivityWithSite })[]>()
          for (const activity of activitiesAtSite) {
            const activityStart = new Date(activity.start)
            const activityEnd = new Date(activity.end)
            const startDay = startOfDay(activityStart)
            const endDay = startOfDay(activityEnd)
            const currentDay = new Date(startDay)
            while (currentDay <= endDay) {
              const dayKey = currentDay.toDateString()
              if (!activitiesByDay.has(dayKey)) activitiesByDay.set(dayKey, [])
              activitiesByDay.get(dayKey)!.push({ ...activity, originalActivity: activity })
              currentDay.setDate(currentDay.getDate() + 1)
            }
          }

          activitiesByDay.forEach((dayActivities) => {
            dayActivities.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
          })

          for (const [dayKey, dayActivities] of activitiesByDay.entries()) {
            const dayStart = startOfDay(new Date(dayKey)).getTime()
            const dayEnd = endOfDay(new Date(dayKey)).getTime()
            const dayDuration = dayEnd - dayStart

            if (dayActivities.length === 1) {
              const activity = dayActivities[0]
              timelineItems.push({
                id: `activity_${asset.id}_${activity.id}_${dayKey}`,
                group: `site-${siteId}-asset-${asset.id}`,
                title: activity.kind.charAt(0).toUpperCase() + activity.kind.slice(1).replace("_", " "),
                start_time: dayStart,
                end_time: dayEnd,
                itemType: "activity",
                itemProps: {
                  "data-activity-id": activity.id,
                  "data-activity-kind": activity.kind,
                  "data-activity-status": activity.status,
                  "data-type": "activity",
                },
                activity,
                asset,
              })
            } else {
              const slotDuration = dayDuration / dayActivities.length
              dayActivities.forEach((activity, index) => {
                const slotStart = dayStart + index * slotDuration
                const slotEnd = dayStart + (index + 1) * slotDuration
                timelineItems.push({
                  id: `activity_${asset.id}_${activity.id}_${dayKey}_${index}`,
                  group: `site-${siteId}-asset-${asset.id}`,
                  title: activity.kind.charAt(0).toUpperCase() + activity.kind.slice(1).replace("_", " "),
                  start_time: slotStart,
                  end_time: slotEnd,
                  itemType: "activity",
                  itemProps: {
                    "data-activity-id": activity.id,
                    "data-activity-kind": activity.kind,
                    "data-activity-status": activity.status,
                    "data-type": "activity",
                  },
                  activity,
                  asset,
                })
              })
            }
          }
        }
      }
    }

    return {
      groups: timelineGroups.map((g) => ({
        id: g.id,
        title: g.title,
        groupType: g.groupType,
        siteGroup: g.siteGroup,
        assetData: g.assetData,
        height: g.height,
        siteIndex: g.siteIndex,
      })),
      items: timelineItems,
    }
  }, [siteGroups, expandedSites])

  const groupRenderer = useCallback(
    ({ group }: { group: (typeof groups)[0] }) => {
      const groupType = group.groupType
      const siteGroup = group.siteGroup
      const siteId = siteGroup?.site?.id ?? "__unassigned__"
      const isExpanded = expandedSites[siteId] !== false
      const canExpand = true

      if (groupType === "site-header") {
        return (
          <Box
            className="sites-timeline-site-header"
            onClick={() => toggleSite(siteId)}
            onMouseEnter={() => setHoveredSiteId(siteId)}
            onMouseLeave={() => setHoveredSiteId((prev) => (prev === siteId ? null : prev))}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              height: "100%",
              cursor: "pointer",
              backgroundColor: "#F1F4F5",
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              borderBottom: "1px solid #E5E5E5",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            {canExpand ? isExpanded ? (
              <FaChevronDown style={{ fontSize: "12px", flexShrink: 0 }} />
            ) : (
              <FaChevronRight style={{ fontSize: "12px", flexShrink: 0 }} />
            ) : undefined}
            <HStack justifyContent="space-between" width="100%">
              <span>{group.title}</span>
              {siteGroup?.hasIotData && (
                <Box as="span" title="Consumption data available">
                  <Icon
                    as={FaChartLine}
                    fontSize="14px"
                    color="active"
                  />
                </Box>
              )}</HStack>
          </Box>
        )
      }

      if (groupType === "asset" && group.assetData) {
        const asset = group.assetData.asset as gqlTypes.Asset
        return (
          <Box
            className="sites-timeline-asset-row"
            onMouseEnter={() => setHoveredSiteId(siteId)}
            onMouseLeave={() => setHoveredSiteId((prev) => (prev === siteId ? null : prev))}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              height: "60px",
              fontSize: "14px",
              backgroundColor: "transparent",
            }}
          >
            <Box width="100%" pl={"18px"}><AssetLink asset={asset} openExternal={true} /></Box>
          </Box>
        )
      }

      return <Box p={2}>{group.title}</Box>
    },
    [expandedSites, toggleSite, groups, items]
  )

  const itemRenderer = useCallback(
    ({ item, getItemProps }: any) => {
      const { style, ...otherProps } = getItemProps(item.itemProps || {})
      const originalWidth = typeof style?.width === "string" ? parseFloat(style.width) : style?.width ?? 100
      const originalLeft = typeof style?.left === "string" ? parseFloat(style.left) : style?.left ?? 0

      // Get the site's expanded state and IoT data availability
      const groupId = item.group as string
      const siteIdMatch = groupId.match(/^site-(.+?)-header$/)
      const siteId = siteIdMatch ? siteIdMatch[1] : null
      const isExpanded = siteId ? (expandedSites[siteId] !== false) : true
      const group = groups.find((g: any) => g.id === groupId)
      const hasIotData = group?.siteGroup?.hasIotData ?? false

      if (item.itemType === "consumption_chart") {
        return (
          <Box
            {...otherProps}
            style={{
              ...style,
              position: "absolute",
              top: `calc(${style?.top || '0px'} + 2.5px)`,
              left: 0,
              width: "100%",
              height: "38px",
              pointerEvents: "none",
              zIndex: 5,
              backgroundColor: "transparent",
              border: "none",
            }}
          >
            <ConsumptionLineChart
              points={item.consumptionTimeline || []}
              width={originalWidth}
              height={38}
              visibleTimeStart={visibleTimeStart}
              visibleTimeEnd={visibleTimeEnd}
            />
          </Box>
        )
      }

      if (item.itemType === "order") {
        const order = item.order as gqlTypes.AssetActivityOrder & { quantity?: number; quantityPlanned?: number; start?: string; end?: string }
        const quantity = order.quantity ?? 0
        const quantityPlanned = order.quantityPlanned ?? 0
        const percentPlanned =
          quantity > 0
            ? Math.round((quantityPlanned / quantity) * 100)
            : 100

        return (
          <Box
            {...otherProps}
            className="sites-timeline-order-on-header"
            style={{
              ...style,
              top: `calc(${style?.top || '0px'} + ${isExpanded && hasIotData ? '45px' : '2.5px'})`,
              left: originalLeft,
              width: originalWidth,
              height: "25px",
              boxSizing: "border-box",
              borderRadius: "6px",
              padding: "0 10px",
              fontSize: "13px",
              fontWeight: 500,
              border: "none",
              backgroundColor: "#E0F2FE",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              zIndex: 10,
              cursor: "default",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              color: "primary",
            }}
            onMouseEnter={(e: React.MouseEvent) => {
              orderHoverOverlayRef.current?.show(
                order,
                quantity,
                quantityPlanned,
                percentPlanned,
                order.start ?? null,
                order.end ?? null,
                e.clientX,
                e.clientY
              )
            }}
            onMouseMove={(e: React.MouseEvent) => {
              orderHoverOverlayRef.current?.updatePosition(e.clientX, null)
            }}
            onMouseLeave={() => orderHoverOverlayRef.current?.hide()}
          >
            <Box
              fontSize={"12px"}
              fontWeight={"semibold"}
              display={originalWidth < 115 ? "none" : "flex"}
              alignItems={"anchor-center"}
              overflowWrap="break-word"
              wordBreak="break-word"
              flexShrink={1}
              minHeight={0}
              overflow="hidden"

            >
              Order: {order.name}
            </Box>
            <Center height="100%" display={originalWidth <= 115 ? "flex" : "none"} >
              <Icon as={FaInfo} fontSize="12px" />
            </Center>
          </Box>
        )
      }

      if (item.itemType === "fulfilment") {
        const fulfilment = item.activity as ActivityWithSite
        const assetCount = fulfilment.assets?.length || 0

        return (
          <Box
            {...otherProps}
            className="sites-timeline-fulfilment"
            style={
              {
                ...style,
                top: `calc(${style?.top || '0px'} + ${isExpanded && hasIotData ? '70px' : '27.5px'})`,
                left: originalLeft,
                width: `calc(${originalWidth}px + 1px)`,
                height: "25px",
                boxSizing: "border-box",
                borderRadius: "6px",
                padding: "0 10px",
                fontSize: "13px",
                fontWeight: 500,
                backgroundColor: "#CDEFDD",
                border: "none",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                zIndex: 10,
                cursor: "pointer",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                color: "primary",
                transition: "none",
              }
            }
            onClick={() => {
              if (assetCount > 0 && fulfilment.assets?.[0]) {
                onActivityEdit?.(fulfilment.id, fulfilment.assets[0].id)
              }
            }}

          >
            <Box
              fontSize={"12px"}
              fontWeight={"semibold"}
              display={originalWidth < 115 ? "none" : "flex"}
              alignItems={"anchor-center"}
              overflowWrap="break-word"
              wordBreak="break-word"
              flexShrink={1}
              minHeight={0}
              overflow="hidden"
            >
              {fulfilment.quantity} kg
            </Box>
            <Center height="100%" display={originalWidth <= 115 ? "flex" : "none"}>
              <Icon as={FaInfo} fontSize="12px" />
            </Center>
          </Box>
        )
      }

      if (item.itemType === "suggested_fulfilment") {
        const suggested = item.suggestedFulfilment as SuggestedFulfilment
        const isFirstSuggestion = item.itemProps?.["data-is-first"] === true
        const numberOfDays = Math.round((new Date(suggested.suggested_end_date).getTime() - new Date(suggested.suggested_start_date).getTime()) / (1000 * 60 * 60 * 24))
        const showConfirm = isFirstSuggestion && onConfirmSuggestedFulfilment
        const widthLimit = showConfirm ? 225 : 130 

        return (
          <Box
            {...otherProps}
            className="sites-timeline-suggested-fulfilment"
            style={{
              ...style,
              top: `calc(${style?.top || '0px'} + ${isExpanded && hasIotData ? '70px' : '27.5px'})`,
              left: originalLeft,
              width: `calc(${originalWidth}px + 1px)`,
              height: "25px",
              boxSizing: "border-box",
              borderRadius: "6px",
              border: "2px dashed",
              borderColor: "linear-gradient(100deg, #39aed2 0%, #a170d9 100%)",
              padding: "0 10px 0 10px",
              fontSize: "13px",
              fontWeight: 500,
              backgroundColor: "rgba(205, 239, 221, 0.4)",
              opacity: 0.7,
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              zIndex: 9,
              cursor: "default",
              overflow: "hidden",
              whiteSpace: "nowrap",
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "center",
              gap: "4px",
              minHeight: 0,
              color: "primary",
            }}
            title={`Suggested: ${suggested.suggested_quantity} kg
Days: ${(new Date(suggested.suggested_end_date).getTime() - new Date(suggested.suggested_start_date).getTime()) / (1000 * 60 * 60 * 24) + 1}
Confidence: ${Math.round(suggested.confidence * 100)}%`}
          >
            {showConfirm && (
              <Button
                size="2xs"
                mt={"1px"}
                height="20px !important"
                fontSize="12px"
                variant="hydrologiq_primary"
                colorPalette="green"
                onClick={(e) => {
                  e.stopPropagation()
                  onConfirmSuggestedFulfilment(suggested)
                }}>
                Confirm
              </Button>
            )}
            <Box
              fontSize={"12px"}
              fontWeight={"semibold"}
              display={originalWidth <= widthLimit ? "none" : "flex"}
              alignItems={"center"}
              flexShrink={1}
              minHeight={0}
              overflow="hidden"
            >
              {suggested.suggested_quantity} kg ({numberOfDays + 1} days)
            </Box>

            <Center height="100%" display={originalWidth <= widthLimit ? "flex" : "none"}>
              <Icon as={FaInfo} fontSize="12px" />
            </Center>

          </Box>
        )
      }

      const activityKind = item.itemProps?.["data-activity-kind"] as keyof typeof ACTIVITY_KIND_CONFIGS
      const settings = ACTIVITY_KIND_CONFIGS[activityKind] || ACTIVITY_KIND_CONFIGS.maintenance
      const isFulfilment = activityKind === "fulfilment"
      const hasCollection = item.activity?.has_collection || false
      const hasDelivery = item.activity?.has_delivery || false
      const missingCollection = isFulfilment && !hasCollection
      const missingDelivery = isFulfilment && !hasDelivery

      const sameActivityItems = items.filter(
        (i: any) => i.itemProps?.["data-activity-id"] === item.itemProps?.["data-activity-id"]
      )
      const sorted = [...sameActivityItems].sort((a, b) => a.start_time - b.start_time)
      const idx = sorted.findIndex((i: any) => i.id === item.id)
      const isFirst = idx === 0
      const isLast = idx === sorted.length - 1
      const borderRadius = "6px"

      return (
        <Box
          {...otherProps}
          aria-label={`${item.asset?.name} ${formatActivityKind(item.activity?.kind)} (${formatDateTime(
            item.activity?.start
          )} - ${formatDateTime(item.activity?.end)})`}
          role="tooltip"
          style={{
            ...style,
            width: originalWidth,
            left: originalLeft,
            height: "55px",
            cursor: "pointer",
            borderRadius: "0px !important",
            border: "none",
            backgroundColor: settings.backgroundColor,
            color: settings.textColor,
            boxSizing: "border-box",
            fontSize: "14px",
            fontWeight: 500,
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            zIndex: isFulfilment ? 5 : 10,
            borderLeft: isFirst
              ? missingDelivery
                ? "4px solid #EF4444"
                : `4px solid ${settings.textColor}`
              : undefined,
            borderRight: isLast && missingCollection ? "4px solid #EF4444" : undefined,
            borderTopRightRadius: isLast ? borderRadius : undefined,
            borderBottomRightRadius: isLast ? borderRadius : undefined,
            borderTopLeftRadius: isFirst ? borderRadius : undefined,
            borderBottomLeftRadius: isFirst ? borderRadius : undefined,
          }}
          onMouseEnter={(e: React.MouseEvent) => {
            hoverOverlayRef.current?.show(item.activity, e.clientX, e.clientY, settings)
          }}
          onMouseMove={(e: React.MouseEvent) => {
            hoverOverlayRef.current?.updatePosition(e.clientX, null)
          }}
          onMouseLeave={() => hoverOverlayRef.current?.hide()}
          onClick={(e: React.MouseEvent) => {
            hoverOverlayRef.current?.hide()
            e.stopPropagation()
            const activityId = item.itemProps?.["data-activity-id"]
            const assetId = item.asset?.id
            if (activityId && assetId) onActivityEdit?.(activityId, assetId)
          }}
        />
      )
    },
    [items, onActivityEdit, groups, expandedSites, suggestedFulfilments]
  )

  // Render activity text labels as DOM overlays
  useEffect(() => {
    if (!containerRef.current) return

    const scrollContainer = containerRef.current.querySelector(".rct-scroll")
    if (!scrollContainer) return

    // Function to render overlays
    const renderOverlays = () => {
      // Remove existing text overlays
      scrollContainer.querySelectorAll(".activity-text-overlay").forEach((el) => el.remove())

      // Group items by activity ID to find bounds
      const activityBounds = new Map<
        string,
        {
          minLeft: number
          maxRight: number
          top: number
          height: number
          displayText: string
          settings: { backgroundColor: string; textColor: string; icon: any }
          cellCount: number
        }
      >()

      // Get timeline dimensions for calculations
      const containerWidth = scrollContainer.clientWidth
      const totalRange = visibleTimeEnd - visibleTimeStart

      // Calculate cumulative top positions for each row based on actual heights
      const rowTopPositions = new Map<string, number>()
      let cumulativeTop = 0
      groups.forEach((group) => {
        rowTopPositions.set(group.id, cumulativeTop)
        cumulativeTop += group.height
      })

      // Calculate bounds for each unique activity
      items.forEach((item) => {
        if (item.itemType !== "activity") return

        const activityId = item.itemProps?.["data-activity-id"] as string
        const activityKind = item.itemProps?.["data-activity-kind"] as keyof typeof ACTIVITY_KIND_CONFIGS

        // Determine display text - always show something for all activity kinds
        let displayText = ""
        displayText = item.asset?.name ? (item.asset?.name.length > 10 ? item.asset?.name.slice(0, 10) + "..." : item.asset?.name) + " - " : ""

        if (activityKind === "fulfilment") {
          displayText += item.activity?.customers?.[0]?.name || ""
        } else if (activityKind === "filling") {
          displayText += item.activity?.site?.name || ""
        }

        // Add quantity - always show it, even if 0
        const quantity = item.activity?.quantity || 0
        displayText += quantity ? ` (${quantity} kg)` : ""

        // Calculate pixel position
        const leftPx = ((item.start_time - visibleTimeStart) / totalRange) * containerWidth
        const rightPx = ((item.end_time - visibleTimeStart) / totalRange) * containerWidth

        // Get the actual top position for this group
        const topPx = rowTopPositions.get(item.group) ?? 0

        // Get the height for this group
        const group = groups.find((g) => g.id === item.group)
        const rowHeight = group?.height ?? 60

        // Get settings for this activity kind
        const settings = ACTIVITY_KIND_CONFIGS[activityKind] || ACTIVITY_KIND_CONFIGS.maintenance

        if (!activityBounds.has(activityId)) {
          activityBounds.set(activityId, {
            minLeft: leftPx,
            maxRight: rightPx,
            top: topPx,
            height: rowHeight,
            displayText,
            settings,
            cellCount: 1,
          })
        } else {
          const bounds = activityBounds.get(activityId)!
          bounds.minLeft = Math.min(bounds.minLeft, leftPx)
          bounds.maxRight = Math.max(bounds.maxRight, rightPx)
          bounds.cellCount += 1
        }
      })

      // Create overlay elements for each activity
      activityBounds.forEach((bounds, activityId) => {
        // Skip if less than 1 cell
        if (bounds.cellCount < 1) return

        const overlay = document.createElement("div")
        overlay.className = "activity-text-overlay"

        // Render the entire overlay content using React
        const IconComponent = bounds.settings.icon

        // Determine what to show based on cell count
        // <= 2 cells: show text if available, otherwise show icon
        // > 2 cells: show both icon and text
        const showIcon = bounds.cellCount > 2
        const showText = bounds.displayText

        const overlayContent = renderToStaticMarkup(
          <div
            style={{
              position: "absolute",
              left: `${bounds.minLeft}px`,
              top: `${bounds.top}px`,
              width: `${bounds.maxRight - bounds.minLeft}px`,
              height: `${bounds.height}px`,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              paddingLeft: "12px",
              paddingRight: "12px",
              color: bounds.settings.textColor,
              fontSize: "14px",
              fontWeight: "500",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              zIndex: 100,
            }}
          >
            {showIcon && IconComponent && <IconComponent style={{ flexShrink: 0, fontSize: "12px" }} />}
            {showText && bounds.displayText && (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{bounds.displayText}</span>
            )}
            {/* If <= 2 cells and no text, show icon as fallback */}
            {!showText && !showIcon && IconComponent && <IconComponent style={{ flexShrink: 0, fontSize: "12px" }} />}
          </div>
        )

        overlay.innerHTML = overlayContent
        scrollContainer.appendChild(overlay)
      })
    }

    // Initial render
    renderOverlays()

    // Set up ResizeObserver to recalculate on container size changes
    const resizeObserver = new ResizeObserver(() => {
      renderOverlays()
    })
    resizeObserver.observe(scrollContainer)

    // Cleanup function
    return () => {
      resizeObserver.disconnect()
      scrollContainer.querySelectorAll(".activity-text-overlay").forEach((el) => el.remove())
    }
  }, [items, groups, visibleTimeStart, visibleTimeEnd])

  // Sync data attributes onto library DOM rows for CSS-driven alternating colours,
  // hover highlights, and site dividers (R1–R5)
  useEffect(() => {
    if (!containerRef.current) return

    const sidebar = containerRef.current.querySelector(".rct-sidebar")
    const horizontalLines = containerRef.current.querySelector(".rct-horizontal-lines")
    if (!sidebar || !horizontalLines) return

    const sidebarRows = sidebar.querySelectorAll(".rct-sidebar-row")
    const hlRows = horizontalLines.querySelectorAll(".rct-hl-even, .rct-hl-odd")

    // Build a lookup: for each group index, what siteId, isEven, and isFirstRowOfSite
    const seenSites = new Set<string>()
    const groupMeta = groups.map((g) => {
      const siteId = g.siteGroup?.site?.id ?? "__unassigned__"
      const isEven = ((g.siteIndex ?? 0) % 2 === 0).toString()
      const isFirstRow = !seenSites.has(siteId)
      if (isFirstRow) seenSites.add(siteId)
      return { siteId, isEven, isFirstRow: isFirstRow.toString(), isHovered: (hoveredSiteId === siteId).toString() }
    })

    // Attach hover listeners to canvas horizontal-line rows
    const hlEnterHandlers: Array<() => void> = []
    const hlLeaveHandlers: Array<() => void> = []

    groupMeta.forEach((meta, i) => {
      const sidebarRow = sidebarRows[i] as HTMLElement | undefined
      const hlRow = hlRows[i] as HTMLElement | undefined

      if (sidebarRow) {
        sidebarRow.setAttribute("data-site-even", meta.isEven)
        sidebarRow.setAttribute("data-site-first-row", meta.isFirstRow)
        sidebarRow.setAttribute("data-site-hovered", meta.isHovered)
        sidebarRow.setAttribute("data-site-id", meta.siteId)
      }
      if (hlRow) {
        hlRow.setAttribute("data-site-even", meta.isEven)
        hlRow.setAttribute("data-site-first-row", meta.isFirstRow)
        hlRow.setAttribute("data-site-hovered", meta.isHovered)
        hlRow.setAttribute("data-site-id", meta.siteId)

        // Canvas-side hover listeners
        const enterHandler = () => setHoveredSiteId(meta.siteId)
        const leaveHandler = () => setHoveredSiteId((prev) => (prev === meta.siteId ? null : prev))
        hlRow.addEventListener("mouseenter", enterHandler)
        hlRow.addEventListener("mouseleave", leaveHandler)
        hlEnterHandlers.push(enterHandler)
        hlLeaveHandlers.push(leaveHandler)
      }
    })

    return () => {
      // Clean up canvas hover listeners
      groupMeta.forEach((_, i) => {
        const hlRow = hlRows[i] as HTMLElement | undefined
        if (hlRow && hlEnterHandlers[i]) {
          hlRow.removeEventListener("mouseenter", hlEnterHandlers[i])
          hlRow.removeEventListener("mouseleave", hlLeaveHandlers[i])
        }
      })
    }
  }, [groups, hoveredSiteId])

  const hasValidTimeRange =
    !isNaN(visibleTimeStart) &&
    !isNaN(visibleTimeEnd) &&
    visibleTimeStart > 0 &&
    visibleTimeEnd > 0 &&
    visibleTimeEnd > visibleTimeStart

  if (!hasValidTimeRange) {
    return (
      <Box p={4}>
        <VStack gap={3} align="stretch">
          <Box height="40px" width="100%" bg="gray.100" borderRadius="md" />
          <Box height="60px" width="100%" bg="gray.50" borderRadius="md" />
        </VStack>
      </Box>
    )
  }

  return (
    <>
      <Box ref={containerRef} className="sites-timeline-container timeline-container" position="relative" tabIndex={0}>
        <Timeline
          groups={groups}
          items={items}
          visibleTimeStart={visibleTimeStart}
          visibleTimeEnd={visibleTimeEnd}
          minZoom={24 * 60 * 60 * 1000}
          maxZoom={31 * 24 * 60 * 60 * 1000}
          onTimeChange={onTimeChange}
          itemRenderer={itemRenderer}
          groupRenderer={groupRenderer}
          sidebarWidth={250}
          itemHeightRatio={0.9}
          stackItems={false}
          canMove={false}
          canResize={false}
          canSelect={false}
          selected={[]}
          buffer={1}
          dragSnap={0}
          traditionalZoom={true}
          onCanvasClick={() => { }}
          onCanvasDoubleClick={() => { }}
          onCanvasContextMenu={() => { }}
        >
          <TimelineHeaders>
            <SidebarHeader>
              {({ getRootProps }: any) => (
                <HStack {...getRootProps()} p={3} justifyContent="space-between" width="100%">
                  <Heading color="primary">Site</Heading>
                </HStack>
              )}
            </SidebarHeader>
            <DateHeader unit="primaryHeader" style={{ color: "#2B2B2B !important" }} />
            <DateHeader unit="day" />
          </TimelineHeaders>
        </Timeline>
      </Box>
      <ActivityHoverOverlay ref={hoverOverlayRef} />
      <OrderHoverOverlay ref={orderHoverOverlayRef} />
    </>
  )
}


// Memoize the component to prevent re-renders when parent updates
export default React.memo(SitesTimelineView, (prevProps, nextProps) => {
  // Deep comparison for arrays and Sets
  const filteredAssetIdsEqual =
    prevProps.filteredAssetIds.length === nextProps.filteredAssetIds.length &&
    prevProps.filteredAssetIds.every((id, index) => id === nextProps.filteredAssetIds[index])



  // Return TRUE to SKIP re-render, FALSE to re-render
  const shouldSkipRender =
    prevProps.visibleTimeStart === nextProps.visibleTimeStart &&
    prevProps.visibleTimeEnd === nextProps.visibleTimeEnd &&
    filteredAssetIdsEqual &&
    prevProps.data === nextProps.data &&
    prevProps.onTimeChange === nextProps.onTimeChange &&
    prevProps.onActivityEdit === nextProps.onActivityEdit &&
    prevProps.suggestedFulfilments === nextProps.suggestedFulfilments &&
    prevProps.siteResponses === nextProps.siteResponses &&
    prevProps.orders === nextProps.orders

  return shouldSkipRender
})
